import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, RotateCcw } from 'lucide-react';
import { ChatCircleDots } from '@phosphor-icons/react';
import AppIcon from '../AppIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const newSession = () => `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const ConciergeTestChat = () => {
  const [sid, setSid] = useState(newSession);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText(''); setBusy(true);
    setMsgs((m) => [...m, { role: 'user', text: q }, { role: 'assistant', text: '' }]);
    const append = (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + t }; return c; });
    try {
      const res = await fetch(`${API}/concierge/test-chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('wf_token') || ''}` },
        body: JSON.stringify({ session_id: sid, message: q }),
      });
      if (!res.ok) throw new Error('The test chat is unavailable right now.');
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n\n'); buf = lines.pop();
        lines.forEach((l) => {
          if (!l.startsWith('data:')) return;
          const ev = JSON.parse(l.slice(5));
          if (ev.delta) append(ev.delta);
          if (ev.error) append(ev.error);
        });
      }
    } catch (err) { append(err.message); } finally { setBusy(false); }
  };

  return (
    <section data-testid="concierge-test-chat" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5 flex flex-col self-start w-full">
      <div className="flex items-center gap-3 mb-3">
        <AppIcon icon={ChatCircleDots} tone="wine" size="md" reveal />
        <div className="flex-1">
          <h3 className="font-serif text-xl text-[#2A2A2A]">Test my Concierge</h3>
          <p className="text-xs text-[#6A6A6A]">Ask what a guest would ask. Answers use your saved script and details.</p>
        </div>
        <button data-testid="concierge-chat-reset" onClick={() => { setSid(newSession()); setMsgs([]); }} title="New conversation" className="text-[#9A8A7E] hover:text-[#7B2C3B]"><RotateCcw className="w-4 h-4" /></button>
      </div>
      <div data-testid="concierge-chat-messages" className="h-72 overflow-auto rounded-xl bg-[#FBF7F2] border border-[#F2E8DE] p-3 space-y-2">
        {!msgs.length && <p className="text-xs text-[#9A8A7E]">Try: “Is there parking?” or “Can I bring my kids?”</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <p data-testid={`concierge-chat-msg-${i}`} className={`max-w-[85%] text-sm px-3 py-2 rounded-2xl whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#7B2C3B] text-white rounded-br-md' : 'bg-white border border-[#EFE3D8] text-[#2A2A2A] rounded-bl-md'}`}>
              {m.text || <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            </p>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2">
        <input data-testid="concierge-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask like a guest would…" className="flex-1 px-4 py-2.5 rounded-full border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]" />
        <button data-testid="concierge-chat-send" disabled={busy || !text.trim()} className="btn-primary rounded-full px-4 inline-flex items-center gap-1 text-sm disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </section>
  );
};

export default ConciergeTestChat;
